import CloseIcon from '@mui/icons-material/Close';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { UserProfile } from './../services/api/type';

const MAX_FILE_SIZE = 1048576; // 1 MB
const MIN_IMAGE_DIMENSIONS = 150; // Dimensiones mínimas
const MAX_IMAGE_WIDTH = 500; // Ancho máximo para la imagen redimensionada
const MAX_IMAGE_HEIGHT = 500; // Alto máximo para la imagen redimensionada

interface NewUserFormProps {
  onSubmit: (newUser: { username: string; email: string; name: string; lastName: string; avatar: string }) => Promise<void>;
  onClose: () => void;
}

const NewUserForm: React.FC<NewUserFormProps> = ({ onSubmit, onClose }) => {
  const [user, setUser] = useState<UserProfile>({
    id: 0,
    username: "",
    email: "",
    googleId: null,
    name: "",
    lastName: "",
    avatar: "",
    status: "ACTIVE",
    role: "",
    roleId: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    confirmPassword: "",
    postalCode: "",
    phone: "",
    curp: "",
    cargo: "",
    estado: "",
    alcaldiaMunicipio: "",
    barrioColonia: "",
    codigoArea: "",
    city: "",
    accessLevel: "",
    telefono: "",
  });

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [isTfjaDomain, setIsTfjaDomain] = useState(false);

  // Efecto para detectar cuando el email tiene el dominio @tfja.gob.mx
  useEffect(() => {
    setIsTfjaDomain(user.email.toLowerCase().endsWith('@tfja.gob.mx'));
  }, [user.email]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser(prevUser => ({ ...prevUser, [name]: value }));


  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (file.size > MAX_FILE_SIZE) {
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = () => {
          const { width, height } = img;

          if (width < MIN_IMAGE_DIMENSIONS || height < MIN_IMAGE_DIMENSIONS) {
            return;
          }

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let newWidth = width;
          let newHeight = height;

          if (width > MAX_IMAGE_WIDTH || height > MAX_IMAGE_HEIGHT) {
            if (width > height) {
              newWidth = MAX_IMAGE_WIDTH;
              newHeight = (height * MAX_IMAGE_WIDTH) / width;
            } else {
              newHeight = MAX_IMAGE_HEIGHT;
              newWidth = (width * MAX_IMAGE_HEIGHT) / height;
            }
          }

          canvas.width = newWidth;
          canvas.height = newHeight;

          if (ctx) {
            ctx.drawImage(img, 0, 0, newWidth, newHeight);
            canvas.toBlob(
              (blob) => {
                if (blob && blob.size > MAX_FILE_SIZE) {
                  return;
                }

                const newFileReader = new FileReader();
                newFileReader.onloadend = () => {
                  setAvatarPreview(newFileReader.result as string);
                  setUser((prevUser) => ({
                    ...prevUser,
                    avatar: newFileReader.result as string,
                  }));
                };
                if (blob) newFileReader.readAsDataURL(blob);
              },
              'image/jpeg',
              0.7
            );
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  const { confirmPassword, ...userData } = user;

    const requiredUserData = {
      username: userData.email,
      email: userData.email,
      name: userData.name,
      lastName: userData.lastName,
      avatar: userData.avatar || "default-avatar-url",
      password: "DefaultPassword123!"
    };

    try {
      setLoading(true);
      await onSubmit(requiredUserData);
    } catch (error) {
      console.error("Error al crear el usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 0,
      backgroundColor: 'transparent',
      minHeight: '0'
    }}>
      <Card sx={{
        maxWidth: 1000,
        width: '100%',
        boxShadow: 6,
        borderRadius: 2
      }}>
        <CardContent sx={{ padding: 4 }}>
          {/* Encabezado del modal */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 3
          }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 500,
                color: '#1f2937',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '20px',
                lineHeight: '100%'
              }}
            >
              Adicionar usuario
            </Typography>
              <IconButton
              onClick={onClose}
              sx={{
                color: '#9ca3af',
                '&:hover': { color: '#6b7280' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Contenedor de la imagen */}
              <Grid item xs={12} md={4}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <Box sx={{
                    position: 'relative',
                    width: 160,
                    height: 160,
                    border: '2px solid #d1d5db',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    backgroundColor: '#F4F5FA'
                  }}>
                    {avatarPreview ? (
                      <Box
                        component="img"
                        src={avatarPreview}
                        alt="Avatar Preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        color: '#002E74'
                      }}>
                        <PhotoCamera sx={{ fontSize: 64, mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                          Subir foto
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af', mt: 0.5 }}>
                          Formatos: jpeg, png
                        </Typography>
                      </Box>
                    )}
                    <input
                      accept="image/*"
                      id="avatar-upload"
                      type="file"
                      onChange={handleImageChange}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Campos de texto */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                     <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: '#374151',
                        mb: 1,
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '16px',
                        lineHeight: '100%'
                      }}
                    >
                      Nombre
                    </Typography>
                    <TextField
                      fullWidth
                      name="name"
                      placeholder="Escribe el nombre aquí"
                      value={user.name}
                      onChange={handleChange}
                      required
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#3b82f6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: '#374151',
                        mb: 1,
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '16px',
                        lineHeight: '100%'
                      }}
                    >
                      Apellido
                    </Typography>
                    <TextField
                      fullWidth
                      name="lastName"
                      placeholder="Escribe el apellido aquí"
                      value={user.lastName}
                      onChange={handleChange}
                      required
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#3b82f6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: '#374151',
                        mb: 1,
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '16px',
                        lineHeight: '100%'
                      }}
                    >
                      E-mail
                    </Typography>
                    <TextField
                      fullWidth
                      name="email"
                      type="email"
                      placeholder="Escribe el e-mail aquí"
                      value={user.email}
                      onChange={handleChange}
                      required
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#3b82f6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                          },
                        },
                      }}
                    />
                    {isTfjaDomain && (
                      <Typography variant="caption" sx={{ color: 'green', mt: 1, display: 'block' }}>
                        Usuario con dominio oficial.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>


            </Grid>

            {/* Botones de acción */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              mt: 4
            }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  flex: 1,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  borderColor: '#d1d5db',
                  color: '#374151',
                  '&:hover': {
                    backgroundColor: '#f9fafb',
                    borderColor: '#9ca3af'
                  }
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  flex: 1,
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  backgroundColor: '#002E74',
                  '&:hover': {
                    backgroundColor: '#1e40af'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  }
                }}
              >
                {loading ? 'Creando...' : 'Adicionar'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Notification handled by parent (UserManagement) */}
    </Box>
  );
};

export default NewUserForm;